import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { useCancelPlanContext } from "./cancel-provider";
import { useUpgradePlan } from "@/hooks/use-subscription";
import { useState } from "react";
import { PaymentInterval, UpdatePlanDtoType } from "@/types/subscription.types";
import { toast } from "sonner";
import { PaymentIntervalWithoutCustom2 } from "@/stores/downgrade-store";
import { motion } from "framer-motion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Calendar,
  Headphones,
  Info,
  Loader2,
  Sliders,
  TrendingDown,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link } from "@tanstack/react-router";

const CancelOptimizationPlan = () => {
  const { subscription, planDetails, searchParams, state, handleStateChange } =
    useCancelPlanContext();

  const { mutateAsync: upgradePlan, isPending: isUpgradePending } =
    useUpgradePlan();

  const [showBillingCycleConfirmation, setShowBillingCycleConfirmation] =
    useState(false);

  const [targetBillingInterval, setTargetBillingInterval] =
    useState<PaymentInterval | null>(null);

  const isAnnual = subscription?.interval === PaymentInterval.ANNUAL;
  const isMonthly = subscription?.interval === PaymentInterval.MONTHLY;
  const isQuarterly = subscription?.interval === PaymentInterval.QUARTERLY;

  // Handle billing cycle upgrade
  const handleConfirmUpgradeBillingCycle = async (
    targetInterval: PaymentInterval
  ) => {
    if (!subscription || !planDetails) {
      toast.error("Missing subscription information");
      return;
    }

    if (planDetails?.isCustomPlan) {
      toast.error("Custom plans cannot be upgraded");
      return;
    }

    try {
      // Create payload for upgrading the billing cycle
      const upgradePayload: UpdatePlanDtoType = {
        subscriptionId: subscription._id,
        currentPlanId: planDetails.planId?._id,
        changeType: "billing-cycle",
        newInterval: targetInterval as PaymentIntervalWithoutCustom2,
        quantity: searchParams.quantity,
        assignedMailboxIds: state.selectedAssignedMailboxes,
      };

      // Call the upgrade mutation
      await upgradePlan(upgradePayload);

      toast.success(
        `Successfully switched to ${targetInterval.toLowerCase()} billing`
      );

      window.cioanalytics?.track("plan_upgraded", {
        plan: planDetails.planId?.planType,
        subscriptionId: subscription._id,
        quantity: searchParams.quantity,
        assignedMailboxCount: planDetails.assignedMailboxes.length,
        changeType: "billing-cycle",
        newInterval: targetInterval,
      });
    } catch (error) {
      console.error("Error upgrading plan:", error);
      toast.error("Failed to update billing cycle. Please try again.");
    }
  };

  const handleBillingCycleChange = (targetInterval: PaymentInterval) => {
    setTargetBillingInterval(targetInterval);
    setShowBillingCycleConfirmation(true);
  };

  const handleBack = () => handleStateChange({ step: 1 });
  const handleNext = () => handleStateChange({ step: 3 });

  if (!planDetails || !subscription) {
    return <div>No plan details or subscription found</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div>
        <h2 className="text-xl font-semibold">Optimization Options</h2>
        <p className="text-muted-foreground mb-4">
          We have several ways to help you save while keeping your current{" "}
          {planDetails?.planId?.planName} plan features.
        </p>

        <Alert variant="info">
          <Info className="h-4 w-4 " />
          <AlertTitle>Stay with Your Current Plan</AlertTitle>
          <AlertDescription>
            <p>
              With your current {planDetails.planId?.planName} plan and enjoy
              these benefits :
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>
                Maintain access to {planDetails.maxWarmupEmailsDaily} warmup
                emails/day
              </li>
              <li>
                Continue using {planDetails.maxWarmupEmailsMonthly} warmup
                emails/month
              </li>
            </ul>
            <Link to="/dashboard" replace>
              <Button variant="outline" className="mt-3">
                Return to Dashboard
              </Button>
            </Link>
          </AlertDescription>
        </Alert>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Stay on your current plan and save</CardTitle>
          <CardDescription>
            We have several options to help you optimize costs while keeping
            your premium features
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isAnnual && (
            <RecomendationCard
              title="Switch to annual billing"
              description="Save 20% by switching to annual billing!"
              icon={<Calendar className="h-5 w-5 text-green-600" />}
              actionButton={
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() =>
                    handleBillingCycleChange(PaymentInterval.ANNUAL)
                  }
                  disabled={isUpgradePending}
                >
                  {isUpgradePending &&
                  targetBillingInterval === PaymentInterval.ANNUAL ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    "Switch to Annual"
                  )}
                </Button>
              }
            />
          )}

          {isMonthly && (
            <RecomendationCard
              title="Switch to quarterly billing"
              description="Save 10% by switching to quarterly billing!"
              icon={<Calendar className="h-5 w-5 text-green-600" />}
              actionButton={
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() =>
                    handleBillingCycleChange(PaymentInterval.QUARTERLY)
                  }
                  disabled={isUpgradePending}
                >
                  {isUpgradePending &&
                  targetBillingInterval === PaymentInterval.QUARTERLY ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    "Switch to Quarterly"
                  )}
                </Button>
              }
            />
          )}

          <RecomendationCard
            title="Consider downgrading instead?"
            description="If you're looking to reduce costs, you might want to consider a lower tier plan:"
            icon={<TrendingDown className="h-5 w-5 text-green-600" />}
            actionButton={
              <Link to="/plans">
                <Button variant="outline" size="sm" className="mt-2">
                  Downgrade plan
                </Button>
              </Link>
            }
          />

          <RecomendationCard
            title="Need help optimizing your plan?"
            description="Our team can help you get the most out of your current plan. Schedule a free consultation with our experts."
            icon={<Headphones className="h-5 w-5 text-green-600" />}
            actionButton={
              <a
                href="https://calendly.com/inboxdoctor-sales/new-meeting"
                target="_blank"
              >
                <Button variant="outline" size="sm" className="mt-2">
                  Schedule Consultation
                </Button>
              </a>
            }
          />

          <RecomendationCard
            title="Custom plan adjustment"
            description="We can create a custom plan that fits your exact needs and budget."
            icon={<Sliders className="h-5 w-5 text-green-600" />}
            actionButton={
              <a
                href="https://calendly.com/inboxdoctor-sales/inboxdoctor-custom-plan"
                target="_blank"
              >
                <Button variant="outline" size="sm" className="mt-2">
                  Contact Support
                </Button>
              </a>
            }
          />
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={handleBack}>
            Back
          </Button>
          <Button onClick={handleNext}>Continue</Button>
        </CardFooter>
      </Card>

      {/* Billing Cycle Confirmation Dialog */}
      <Dialog
        open={showBillingCycleConfirmation}
        onOpenChange={setShowBillingCycleConfirmation}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Billing Cycle Change</DialogTitle>
            <DialogDescription>
              Are you sure you want to switch to{" "}
              {targetBillingInterval?.toLowerCase()} billing?
              {targetBillingInterval === PaymentInterval.ANNUAL && (
                <p className="mt-2 text-green-600">
                  You'll save 20% with annual billing!
                </p>
              )}
              {targetBillingInterval === PaymentInterval.QUARTERLY && (
                <p className="mt-2 text-green-600">
                  You'll save 10% with quarterly billing!
                </p>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowBillingCycleConfirmation(false)}
              className="sm:w-auto w-full"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowBillingCycleConfirmation(false);
                if (targetBillingInterval) {
                  handleConfirmUpgradeBillingCycle(targetBillingInterval);
                }
              }}
              className="sm:w-auto w-full"
              disabled={isUpgradePending}
            >
              {isUpgradePending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                "Confirm Change"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

const RecomendationCard = ({
  title,
  description,
  icon,
  actionButton,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  actionButton: React.ReactNode;
}) => {
  return (
    <div className="flex items-start gap-4 p-4 border rounded-lg">
      <div className="bg-green-100 dark:bg-green-900/20 p-2 rounded-full">
        {icon}
      </div>
      <div>
        <h3 className="font-medium">{title}</h3>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>

        {actionButton && actionButton}
      </div>
    </div>
  );
};

export default CancelOptimizationPlan;
